import { useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Loader2, AlertCircle } from "lucide-react";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export type AIProvider = "openai";

interface Props {
  title: string;
  description: string;
  file: File | null;
  setFile: (file: File | null) => void;
  aiProvider: AIProvider;
  setAIProvider: (provider: AIProvider) => void;
  isProcessing: boolean;
  processingStatus: string;
  error: string | null;
  setError: (error: string | null) => void;
  onExtract: () => void;
  onClear: () => void;
}

export function PDFUploadCard({
  title,
  description,
  file,
  setFile,
  aiProvider,
  setAIProvider,
  isProcessing,
  processingStatus,
  error,
  setError,
  onExtract,
  onClear,
}: Props) {
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (newFile: File) => {
    setError(null);

    if (newFile.type !== "application/pdf") {
      setError("Please upload a PDF file");
      return;
    }

    if (newFile.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    setFile(newFile);
  };

  const inputId = `file-input-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors
            ${file ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
            ${isProcessing ? "pointer-events-none opacity-50" : "cursor-pointer"}
          `}
          onClick={() => !isProcessing && document.getElementById(inputId)?.click()}
        >
          <input
            id={inputId}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isProcessing}
          />

          {file ? (
            <div className="flex flex-col items-center gap-1">
              <FileText className="w-8 h-8 text-primary" />
              <p className="font-medium text-sm">{file.name}</p>
              <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <p className="font-medium text-sm">Drop PDF here or click to upload</p>
              <p className="text-xs text-muted-foreground">Max 10MB</p>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        {/* Processing Status */}
        {isProcessing && (
          <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">{processingStatus}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 justify-end">
          {file && !isProcessing && (
            <Button variant="outline" size="sm" onClick={onClear}>
              Clear
            </Button>
          )}
          <Button size="sm" onClick={onExtract} disabled={!file || isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Extract Data"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
